import { Link } from '@tanstack/react-router';
import { Archive, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { CvBankEntryView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Popover } from '../../../components/Popover';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { formatDate } from '../../../lib/format';
import { useCandidateCvBank } from '../api/cv-bank-api';
import { useAddToCvBank, useCvBankAccess, useRemoveFromCvBank } from '../hooks/use-cv-bank-actions';
import styles from '../CvBank.module.css';
import { RemovePanel } from './RemovePanel';

/**
 * CAND-02's CV Bank card (ADR 0046 Q2 a): "In the CV Bank", with the CV kept and Remove — or
 * "Add to CV Bank". The change shows at once and settles when the server answers (§5a rule 1).
 */
export function CandidateCvBankCard({ candidateId, name }: { readonly candidateId: string; readonly name: string }): ReactNode {
  const { canSee, canEdit } = useCvBankAccess();
  const view = useCandidateCvBank(candidateId, canSee);
  const { add } = useAddToCvBank();
  const remove = useRemoveFromCvBank();
  const [pending, setPending] = useState<'adding' | 'removing' | null>(null);
  const [removing, setRemoving] = useState<{ readonly entry: CvBankEntryView; readonly anchor: AnchorRect } | null>(null);

  if (!canSee || !view.data) return null;
  const entries = pending === 'removing' ? [] : view.data.entries;
  const isIn = pending === 'adding' || entries.length > 0;

  const addNow = (): void => {
    setPending('adding');
    void add([{ candidateId, name }], 'profile').finally(() => setPending(null));
  };

  const removeNow = (entry: CvBankEntryView, reason: string): void => {
    setRemoving(null);
    if (view.data?.entries.length === 1) setPending('removing');
    void remove({ kind: 'ids', entries: [{ id: entry.id, name }] }, reason).finally(() => setPending(null));
  };

  return (
    <Card
      title="CV Bank"
      action={
        canEdit && !isIn && view.data.hasCv ? (
          <Button variant="ghost" size="sm" icon={Archive} onClick={addNow}>
            Add to CV Bank
          </Button>
        ) : undefined
      }
    >
      <div className={styles.cardBody}>
        {isIn ? (
          <>
            <span className={styles.cardLine}>
              <Chip tone="success">In the CV Bank</Chip>
              <Link to="/cv-bank" className={styles.name}>
                Open the CV Bank
              </Link>
            </span>
            {entries.map((entry) => (
              <span key={entry.id} className={styles.cardLine}>
                <span>{entry.cv.fileName}</span>
                <span className={styles.meta}>since {formatDate(entry.addedAt)}</span>
                {canEdit && (
                  <Button variant="ghost" size="sm" icon={X} aria-label={`Remove ${entry.cv.fileName} from the CV Bank`} onClick={(event) => setRemoving({ entry, anchor: anchorFrom(event.currentTarget) })}>
                    Remove
                  </Button>
                )}
              </span>
            ))}
          </>
        ) : (
          <p className={styles.meta}>{view.data.hasCv ? 'Not in the CV Bank.' : 'Not in the CV Bank. Upload a CV first to keep them there.'}</p>
        )}
      </div>
      <Popover anchor={removing?.anchor ?? null} onClose={() => setRemoving(null)} align="right" width={360} label="Remove from CV Bank">
        {removing && <RemovePanel who={name} onCancel={() => setRemoving(null)} onRemove={(reason) => removeNow(removing.entry, reason)} />}
      </Popover>
    </Card>
  );
}
